import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Card,
  CardContent,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CancelIcon,
} from '@mui/icons-material';

// Permission modules
const permissionModules = {
  'Employee Management': [
    'View Employees',
    'Add Employees',
    'Edit Employees',
    'Delete Employees',
    'Export Employees'
  ],
  'Leave Management': [
    'View Leaves',
    'Request Leave',
    'Approve Leaves',
    'Manage Leave Settings',
    'Leave Reports'
  ],
  'Attendance': [
    'View Attendance',
    'Mark Attendance',
    'Manage Attendance',
    'Attendance Reports'
  ],
  'Payroll': [
    'View Payroll',
    'Process Payroll',
    'Approve Payroll',
    'Payroll Reports'
  ],
  'Performance': [
    'View Performance',
    'Conduct Reviews',
    'Manage Goals',
    'Performance Reports'
  ],
  'Team Management': [
    'View Team',
    'Manage Team',
    'Team Reports',
    'Assign Tasks'
  ],
  'Documents': [
    'View Documents',
    'Upload Documents',
    'Approve Documents',
    'Delete Documents'
  ],
  'System Settings': [
    'View Settings',
    'Edit Settings',
    'Manage Users',
    'Manage Roles'
  ]
};

// Initial role permissions
const initialRolePermissions = {
  'Administrator': ['all'],
  'Manager': [
    'View Employees', 'Add Employees', 'Edit Employees',
    'View Leaves', 'Approve Leaves', 'Manage Leave Settings',
    'View Attendance', 'Manage Attendance',
    'View Payroll', 'Process Payroll',
    'View Performance', 'Conduct Reviews'
  ],
  'Sales Executive': [
    'View Team', 'Mark Attendance', 'Request Leave', 'View Performance'
  ],
  'HR Executive': [
    'View Employees', 'Add Employees', 'Edit Employees',
    'View Leaves', 'Approve Leaves',
    'View Attendance', 'Manage Attendance'
  ],
  'Employee': [
    'Mark Attendance', 'Request Leave', 'View Performance', 'View Documents'
  ]
};

const RolePermission = () => {
  const [selectedRole, setSelectedRole] = useState('Manager');
  const [rolePermissions, setRolePermissions] = useState(initialRolePermissions);
  const [editMode, setEditMode] = useState(false);
  const [tempPermissions, setTempPermissions] = useState([]);

  // Get current role permissions
  const currentRolePermissions = rolePermissions[selectedRole] || [];

  // Check if permission is granted
  const hasPermission = (permissionName) => {
    if (editMode) {
      return tempPermissions.includes(permissionName) || currentRolePermissions.includes('all');
    }
    return currentRolePermissions.includes(permissionName) || currentRolePermissions.includes('all');
  };

  // Toggle permission in edit mode
  const togglePermission = (permissionName) => {
    if (!editMode) return;
    
    setTempPermissions(prev => 
      prev.includes(permissionName)
        ? prev.filter(p => p !== permissionName)
        : [...prev, permissionName]
    );
  };

  // Start editing
  const startEdit = () => {
    setTempPermissions([...currentRolePermissions]);
    setEditMode(true);
  };

  // Save changes
  const saveChanges = () => {
    setRolePermissions(prev => ({
      ...prev,
      [selectedRole]: tempPermissions
    }));
    setEditMode(false);
    setTempPermissions([]);
    alert('Permissions updated successfully!');
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditMode(false);
    setTempPermissions([]);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Role Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage role-based access permissions
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            {editMode ? (
              <>
                <Button
                  variant="contained"
                  onClick={saveChanges}
                  startIcon={<SaveIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={cancelEdit}
                  startIcon={<CancelIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={startEdit}
                startIcon={<EditIcon />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Edit Permissions
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Role Selector */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <FormControl fullWidth size="small">
            <InputLabel>Select Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label="Select Role"
            >
              {Object.keys(rolePermissions).map(role => (
                <MenuItem key={role} value={role}>{role}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Permissions List */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={3}>
            {Object.entries(permissionModules).map(([moduleName, permissions]) => (
              <Grid item xs={12} md={6} key={moduleName}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
                    {moduleName}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {permissions.map((permission) => (
                    <Box key={permission}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hasPermission(permission)}
                            onChange={() => togglePermission(permission)}
                            disabled={!editMode}
                            sx={{
                              '&.Mui-disabled': {
                                color: hasPermission(permission) ? '#4caf50' : 'inherit'
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {permission}
                          </Typography>
                        }
                        sx={{
                          width: '100%',
                          m: 0,
                          py: 0.5,
                          '&:hover': {
                            backgroundColor: editMode ? '#f8f9fa' : 'transparent'
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RolePermission;
